import { InlineTextContainer } from '../atoms/Atoms.tsx'
import { ScrollstoryDocumentDataBodyAudioSlice } from '../../../types.generated.ts'
import { getLinkFieldUrl } from '../../data/prismic-helper.ts'
import { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import { PlayIcon, PauseIcon } from '../atoms/Icons'
import { asText } from '@prismicio/client'

export interface ArticleProps {
  data: ScrollstoryDocumentDataBodyAudioSlice
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

export default function InlineAudio(props: ArticleProps) {
  const audioUrl = getLinkFieldUrl(props.data.primary.audio_file)

  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [showProgress, setShowProgress] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (audioRef.current) {
      // Set volume (default to 0.75 if not specified)
      audioRef.current.volume = props.data.primary.audio_volume
        ? props.data.primary.audio_volume / 100
        : 0.75

      audioRef.current.addEventListener('loadedmetadata', () => {
        if (audioRef.current) {
          setDuration(audioRef.current.duration)
        }
      })
      audioRef.current.addEventListener('timeupdate', () => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime)
        }
      })

      // Register media events to track OS playback controls (i.e mac os keyboard play/pause button)
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: props.data.primary.description || 'Audio',
        })

        navigator.mediaSession.setActionHandler('play', () => {
          if (audioRef.current) {
            audioRef.current.play()
            setIsPlaying(true)
            setShowProgress(true)
          }
        })

        navigator.mediaSession.setActionHandler('pause', () => {
          if (audioRef.current) {
            audioRef.current.pause()
            setIsPlaying(false)
          }
        })
      }
    }

    return () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null)
        navigator.mediaSession.setActionHandler('pause', null)
      }
    }
  }, [props.data.primary.description, props.data.primary.audio_volume])

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play()
      }
      setIsPlaying(!isPlaying)
      setShowProgress(true)
    }
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left
      const percentage = x / rect.width
      const newTime = percentage * duration
      audioRef.current.currentTime = newTime
      setCurrentTime(newTime)
    }
  }

  const containerCss =
    asText(props.data.primary.audio_player_container_css) ?? ''

  return (
    <InlineTextContainer data-cy="inlineAudio">
      <audio ref={audioRef} src={audioUrl} />
      <AudioPlayerContainer containerCss={containerCss}>
        <PlayPauseButton onClick={togglePlayPause}>
          {isPlaying ? (
            <PauseIcon width="50px" height="50px" />
          ) : (
            <PlayIcon width="50px" height="50px" />
          )}
        </PlayPauseButton>

        {!showProgress ? (
          <>
            {props.data.primary.description && (
              <>
                <Description>{props.data.primary.description}</Description>
                <span>·</span>
              </>
            )}

            {duration > 0 && <Duration>{formatTime(duration)}</Duration>}

            {props.data.primary.link_text &&
              getLinkFieldUrl(props.data.primary.link_destination) && (
              <Link
                href={getLinkFieldUrl(props.data.primary.link_destination)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {props.data.primary.link_text}
              </Link>
            )}
          </>
        ) : (
          <ProgressContainer>
            <TimeDisplay>{formatTime(currentTime)}</TimeDisplay>
            <ProgressBar onClick={handleProgressClick}>
              <ProgressFill
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              <Knob style={{ left: `${(currentTime / duration) * 100}%` }} />
            </ProgressBar>
            <TimeDisplay>{formatTime(duration)}</TimeDisplay>
          </ProgressContainer>
        )}
      </AudioPlayerContainer>
    </InlineTextContainer>
  )
}

const AudioPlayerContainer = styled.div<{ containerCss?: string }>`
  display: flex;
  align-items: center;
  gap: 20px;
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerContainer}
  ${(props) => props.containerCss}
`

const PlayPauseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: #221f1f;
  border-radius: 50%;

  ${({ theme }) => theme.mediaControlsInlineAudioPlayerPlayPauseButton}
`

const Description = styled.span`
  font-size: 0.8em;
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerDescription}
`

const Duration = styled.span`
  font-size: 0.8em;
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerDuration}
`

const Link = styled.a`
  font-size: 0.8em;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerLink}
`

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerProgressBarContainer}
`

const TimeDisplay = styled.span`
  font-size: 0.8em;
  min-width: 45px;
  text-align: center;
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerProgressBarTimeDisplay}
`

const ProgressBar = styled.div`
  flex: 1;
  height: 50px;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    height: 5px;
    background: #ddd;
    border-radius: 2px;
  }
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerProgressBar}
`

const ProgressFill = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 5px;
  background: #221f1f;
  border-radius: 2px;
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerProgressBarFilled}
`

const Knob = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: #221f1f;
  border-radius: 50%;
  ${({ theme }) => theme.mediaControlsInlineAudioPlayerProgressBarKnob}
`
